(() => {
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => [...root.querySelectorAll(sel)];

  let lastDrawerTrigger = null;

  function openDrawer(id) {
    const el = qs(`#${id}`);
    if (!el) return;
    lastDrawerTrigger = document.activeElement;
    if (id === 'FilterDrawer') fillFilterDrawer();
    el.classList.add('is-open');
    el.setAttribute('open', '');
    // the markup ships aria-hidden="true"; a drawer that is on screen must not
    // stay hidden from assistive tech
    el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    const focusable = qs('button, [href], input, select, textarea', el);
    focusable?.focus();
  }

  function closeDrawer(id) {
    const el = qs(`#${id}`);
    if (!el) return;
    el.classList.remove('is-open');
    el.removeAttribute('open');
    el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    // send focus back where it came from, or it lands on <body>
    if (lastDrawerTrigger && document.contains(lastDrawerTrigger)) lastDrawerTrigger.focus();
    lastDrawerTrigger = null;
  }

  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-drawer-open]');
    if (openBtn) {
      e.preventDefault();
      openDrawer(openBtn.getAttribute('data-drawer-open'));
    }
    const closeBtn = e.target.closest('[data-drawer-close]');
    if (closeBtn) {
      e.preventDefault();
      closeDrawer(closeBtn.getAttribute('data-drawer-close'));
    }
    const overlay = e.target.closest('[data-drawer-overlay]');
    if (overlay) {
      closeDrawer(overlay.getAttribute('data-drawer-overlay'));
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      qsa('.drawer.is-open').forEach((d) => {
        d.classList.remove('is-open');
        d.removeAttribute('open');
        d.setAttribute('aria-hidden', 'true');
      });
      document.body.style.overflow = '';
      if (lastDrawerTrigger && document.contains(lastDrawerTrigger)) lastDrawerTrigger.focus();
      lastDrawerTrigger = null;
    }
  });

  async function refreshCartDrawer() {
    const root = qs('#CartDrawer');
    if (!root) return;
    const sectionId = root.dataset.sectionId;
    if (!sectionId) return;
    const res = await fetch(`?sections=${sectionId}`);
    const json = await res.json();
    const html = json[sectionId];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('#CartDrawer');
    if (next) root.replaceWith(next);
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    if (btn) btn.disabled = true;
    const root = window.Shopify?.routes?.root || '/';

    let added = false;
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!res.ok) {
        // 422 = sold out / not enough stock. Surface it; do NOT fall through to
        // a native submit, and do not claim success.
        const body = await res.json().catch(() => ({}));
        showFormError(form, body.description || body.message || 'Sorry, that could not be added.');
        return;
      }
      added = true;
    } catch (err) {
      // network-level failure only — nothing was added, so a native submit is safe
      console.error(err);
      form.submit();
      return;
    } finally {
      if (btn) btn.disabled = false;
    }

    if (!added) return;
    try {
      clearFormError(form);
      await refreshCartDrawer();
      openDrawer('CartDrawer');
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      qsa('[data-cart-count]').forEach((bubble) => {
        bubble.textContent = String(cart.item_count);
        bubble.hidden = cart.item_count === 0;
      });
    } catch (err) {
      // the item IS in the cart; only the UI refresh failed
      console.error(err);
    }
  });

  function showFormError(form, message) {
    if (!form) return;
    let box = form.querySelector('[data-form-error]');
    if (!box) {
      box = document.createElement('p');
      box.className = 'field__error';
      box.setAttribute('data-form-error', '');
      box.setAttribute('role', 'alert');
      form.appendChild(box);
    }
    box.textContent = message;
  }

  function clearFormError(form) {
    form.querySelector('[data-form-error]')?.remove();
  }

  /* ——— Product variant selection ———
     /cart/add.js keys off `id` and ignores `options[...]`, so every option
     change has to rewrite the hidden id, or the shopper is charged for and
     shipped the default variant. ——— */
  function initVariantPicker(form) {
    const mapEl = qs('[data-variant-map]', form);
    if (!mapEl) return;
    // The PDP renders the same product in two forms (main + sticky bar). Only
    // one holds the map, so the picker drives every id input in the section.
    const scope = form.closest('.pdp') || document;
    const idInputs = qsa('[data-variant-id]', scope);
    if (!idInputs.length) return;

    let map;
    try {
      map = JSON.parse(mapEl.textContent);
    } catch (err) {
      console.error('variant map', err);
      return;
    }
    if (!Array.isArray(map?.variants) || !map.variants.length || !Array.isArray(map.options)) return;
    map.variants = map.variants.filter((v) => v && Array.isArray(v.options));
    if (!map.variants.length) return;

    const submits = qsa('.pdp__add', scope);
    const priceOuts = qsa('.pdp__add-price', scope);
    const labelOut = qs('.pdp__swatch-label', scope);

    function selected() {
      return (map.options || []).map((name) => {
        const field = form.querySelector(`[name="options[${CSS.escape(name)}]"]:checked`)
          || form.querySelector(`select[name="options[${CSS.escape(name)}]"]`);
        return field ? field.value : null;
      });
    }

    function apply() {
      const want = selected();
      const match = map.variants.find(
        (v) => v.options.length === want.length && v.options.every((o, i) => o === want[i])
      );
      if (!match) {
        // combination does not exist — block the sale rather than ship a guess
        submits.forEach((b) => {
          b.disabled = true;
          const t = b.querySelector('span');
          if (t) t.textContent = 'Unavailable';
        });
        return;
      }
      idInputs.forEach((i) => { i.value = String(match.id); });
      submits.forEach((b) => {
        b.disabled = !match.available;
        const t = b.querySelector('span');
        if (t) t.textContent = match.available ? 'aDD TO CART' : 'Sold out';
      });
      priceOuts.forEach((el) => { el.textContent = match.price; });
      const colour = want[(map.options || []).findIndex((n) => /colou?r|metal/i.test(n))];
      if (labelOut && colour) labelOut.textContent = colour;
    }

    form.addEventListener('change', (e) => {
      if (e.target.name && e.target.name.startsWith('options[')) apply();
    });
    apply();
  }

  qsa('form[data-product-form]').forEach(initVariantPicker);

  // the customizer replaces section markup wholesale; re-bind or the merchant's
  // preview silently stops switching variants
  document.addEventListener('shopify:section:load', (e) => {
    qsa('form[data-product-form]', e.target).forEach(initVariantPicker);
    qsa('[data-studio]', e.target).forEach(initStudio);
  });

  /* ——— Cart line quantity ———
     One timer PER LINE: a shared timer meant editing a second line inside the
     debounce window silently discarded the first edit. And a non-ok response
     must never leave the input showing a quantity the cart does not hold. ——— */
  const qtyTimers = new WeakMap();

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-qty]');
    if (!input) return;
    clearTimeout(qtyTimers.get(input));
    qtyTimers.set(
      input,
      setTimeout(async () => {
        const root = window.Shopify?.routes?.root || '/';
        const line = input.closest('.cart-line') || input.parentElement;
        const wanted = Number(input.value) || 0;
        try {
          const res = await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: input.dataset.cartQty, quantity: wanted }),
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) {
            // e.g. only 2 in stock. Snap the field back to the truth and say so,
            // rather than leaving it reading a quantity that was never bought.
            const actual = (body.items || []).find((i) => i.key === input.dataset.cartQty);
            if (actual) input.value = String(actual.quantity).padStart(2, '0');
            showFormError(line, body.description || body.message || 'That quantity is unavailable.');
            return;
          }
          window.location.reload();
        } catch (err) {
          console.error(err);
          showFormError(line, 'Could not update the bag. Check your connection and try again.');
        }
      }, 400)
    );
  });

  /* ——— Mobile filter sheet ———
     The sheet ships empty; clone the desktop rail into it on open so there is
     one source of truth for the controls. ids are suffixed to stay unique. ——— */
  function fillFilterDrawer() {
    const target = qs('[data-filter-clone-target]');
    if (!target || target.childElementCount) return;
    const rail = qs('.plp__rail');
    if (!rail) return;
    const clone = rail.cloneNode(true);
    clone.classList.remove('plp__rail');
    clone.classList.add('plp__rail-mobile');
    clone.id = `${rail.id}-mobile`;
    qsa('[id]', clone).forEach((el) => {
      const forEls = qsa(`label[for="${CSS.escape(el.id)}"]`, clone);
      el.id = `${el.id}-mobile`;
      forEls.forEach((l) => l.setAttribute('for', el.id));
    });
    target.appendChild(clone);
  }

  const SAVED_DESIGNS_KEY = 'cahoot:designs';

  const escapeHtml = (v) =>
    String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ——— Saved designs list on the account page ——— */
  qsa('[data-saved-designs]').forEach((list) => {
    let designs = [];
    try {
      designs = JSON.parse(localStorage.getItem(SAVED_DESIGNS_KEY) || '[]');
    } catch {
      designs = [];
    }
    designs = Array.isArray(designs) ? designs.filter(Boolean) : [];
    if (!designs.length) return;
    list.innerHTML = designs
      .map(
        (d) => `
      <li class="cart-line">
        <span class="cart-line__media"><img src="${escapeHtml(d.chain?.src || '')}" alt="" loading="lazy"></span>
        <div class="cart-line__body">
          <p class="cart-line__title">“${escapeHtml(d.name)}”</p>
          <div class="cart-line__meta">
            <p class="cart-line__eyebrow">CUSTOM DESIGN</p>
            <p class="cart-line__spec"><span>${1 + (d.charms?.length || 0) * (d.count || 1)} Products</span></p>
          </div>
          <div class="cart-line__foot">
            <a class="cart-line__studio" href="/pages/studio">Continue</a>
            <p class="cart-line__price">₹${Number(d.total || 0).toLocaleString('en-IN')}</p>
          </div>
        </div>
      </li>`
      )
      .join('');
  });

  /* ——— Wishlist ———
     No wishlist backend exists, so this is local to the browser. It is honest
     about that: the heart reflects what this device saved, nothing more. ——— */
  const WISHLIST_KEY = 'cahoot:wishlist';

  function readWishlist() {
    try {
      return new Set(JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'));
    } catch {
      return new Set();
    }
  }

  function paintWishlist() {
    const saved = readWishlist();
    qsa('[data-wishlist]').forEach((btn) => {
      const on = saved.has(btn.dataset.wishlist);
      btn.setAttribute('aria-pressed', String(on));
      btn.classList.toggle('is-active', on);
    });
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist]');
    if (!btn) return;
    e.preventDefault();
    const saved = readWishlist();
    const handle = btn.dataset.wishlist;
    if (saved.has(handle)) saved.delete(handle);
    else saved.add(handle);
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify([...saved]));
    } catch {
      /* private mode — the toggle still reflects this session */
    }
    paintWishlist();
  });

  paintWishlist();

  /* ——— Saved-address edit disclosure ——— */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-address-toggle]');
    if (!trigger) return;
    e.preventDefault();
    const box = qs(`#${CSS.escape(trigger.dataset.addressToggle)}`);
    if (!box) return;
    box.hidden = !box.hidden;
    trigger.setAttribute('aria-expanded', String(!box.hidden));
  });

  /* ——— Shop the look ———
     Mobile keeps the photo as the hero: cards are overlays a hotspot opens.
     From 1024px the cards live in their own column beside the image (the
     Figma's 654/578 split) and are always visible, so a hotspot highlights its
     card instead of opening a popover. ——— */
  const SHOP_LOOK_DESKTOP = window.matchMedia?.('(min-width: 1024px)');

  function shopLookIsDesktop() {
    return !!SHOP_LOOK_DESKTOP?.matches;
  }

  function closeShopLook(root) {
    qsa('[data-shop-look-hotspot]', root).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    qsa('[data-shop-look-panel]', root).forEach((panel) => {
      panel.classList.remove('is-active');
      // the global [hidden] rule wins over any CSS, so visibility is set here
      panel.hidden = !shopLookIsDesktop();
    });
  }

  function syncShopLook() {
    qsa('[data-shop-look]').forEach((root) => closeShopLook(root));
  }

  SHOP_LOOK_DESKTOP?.addEventListener?.('change', syncShopLook);
  syncShopLook();

  document.addEventListener('click', (e) => {
    const hotspot = e.target.closest('[data-shop-look-hotspot]');
    const root = e.target.closest('[data-shop-look]');
    if (hotspot && root) {
      e.preventDefault();
      const panelId = hotspot.getAttribute('aria-controls');
      const panel = qs(`#${CSS.escape(panelId)}`, root);
      const wasOpen = hotspot.getAttribute('aria-expanded') === 'true';
      closeShopLook(root);
      if (!wasOpen && panel) {
        hotspot.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
        if (shopLookIsDesktop()) {
          panel.classList.add('is-active');
          panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
      return;
    }
    if (root && !e.target.closest('[data-shop-look-panel]')) {
      closeShopLook(root);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    qsa('[data-shop-look]').forEach((root) => closeShopLook(root));
  });

  /* ——— Mega menu (desktop Shop flyout) ——— */
  function closeMegaMenus() {
    qsa('[data-megamenu]').forEach((panel) => { panel.hidden = true; });
    qsa('[data-megamenu-trigger]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
  }

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-megamenu-trigger]');
    if (trigger) {
      e.preventDefault();
      const panel = qs(`#${CSS.escape(trigger.getAttribute('aria-controls'))}`);
      const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
      closeMegaMenus();
      if (!wasOpen && panel) {
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
      }
      return;
    }
    if (!e.target.closest('[data-megamenu]')) closeMegaMenus();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMegaMenus();
  });

  /* ——— Mobile menu levels ——— */
  document.addEventListener('click', (e) => {
    const go = e.target.closest('[data-menu-go]');
    if (!go) return;
    e.preventDefault();
    const panel = go.closest('.drawer__panel');
    if (!panel) return;
    const target = go.getAttribute('data-menu-go');
    qsa('[data-menu-level]', panel).forEach((level) => {
      const on = level.getAttribute('data-menu-level') === target;
      level.classList.toggle('is-active', on);
      level.hidden = !on;
    });
  });

  /* ——— Accordions (PDP tabs, FAQ) ——— */
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-accordion-tab]');
    if (!tab) return;
    e.preventDefault();
    const root = tab.closest('[data-accordion]');
    if (!root) return;
    const name = tab.getAttribute('data-accordion-tab');
    qsa('[data-accordion-tab]', root).forEach((t) => {
      t.classList.toggle('is-active', t === tab);
      t.setAttribute('aria-selected', String(t === tab));
    });
    qsa('[data-accordion-panel]', root).forEach((p) => {
      p.hidden = p.getAttribute('data-accordion-panel') !== name;
    });
  });

  /* ——— Tabs (account, orders, gift guide) ———
     Tab groups nest (account view > orders sub-view), so only act on the
     triggers and panels belonging to *this* group, never a nested one. */
  function ownedBy(root, sel) {
    return qsa(sel, root).filter((el) => el.closest('[data-tabs]') === root);
  }

  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-tab]');
    if (!tab) return;
    e.preventDefault();
    const root = tab.closest('[data-tabs]');
    if (!root) return;
    const name = tab.getAttribute('data-tab');
    ownedBy(root, '[data-tab]').forEach((t) => {
      t.classList.toggle('is-active', t === tab);
      t.setAttribute('aria-selected', String(t === tab));
    });
    ownedBy(root, '[data-tab-panel]').forEach((p) => {
      p.hidden = p.getAttribute('data-tab-panel') !== name;
    });
  });

  /* ——— Disclosure rows (FAQ, filter "More", password recovery) ——— */
  document.addEventListener('click', (e) => {
    const more = e.target.closest('[data-filter-more]');
    if (more) {
      e.preventDefault();
      const group = more.closest('.filter-group');
      qsa('[data-filter-overflow]', group).forEach((li) => { li.hidden = false; });
      more.remove();
      return;
    }
    const recover = e.target.closest('[data-recover-toggle]');
    if (recover) {
      e.preventDefault();
      const box = qs('#RecoverPassword');
      if (box) box.hidden = !box.hidden;
    }
  });

  /* ——— Quantity steppers ——— */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    e.preventDefault();
    const wrap = btn.closest('.qty');
    const input = wrap && qs('input', wrap);
    if (!input) return;
    const min = Number(input.min || 0);
    const max = Number(input.max || 99);
    const step = Number(btn.getAttribute('data-qty-step'));
    const next = Math.min(max, Math.max(min, (Number(input.value) || 0) + step));
    input.value = String(next).padStart(2, '0');
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ——— OTP boxes ——— */
  qsa('[data-otp]').forEach((root) => {
    const boxes = qsa('input[type="text"]', root);
    boxes.forEach((box, i) => {
      box.addEventListener('input', () => {
        box.value = box.value.replace(/\D/g, '').slice(-1);
        if (box.value && boxes[i + 1]) boxes[i + 1].focus();
        const hidden = qs('[data-otp-value]', root);
        if (hidden) hidden.value = boxes.map((b) => b.value).join('');
      });
      box.addEventListener('keydown', (ev) => {
        if (ev.key === 'Backspace' && !box.value && boxes[i - 1]) boxes[i - 1].focus();
      });
    });
    const countdown = qs('[data-otp-countdown]', root);
    if (countdown) {
      let left = Number(countdown.dataset.otpCountdown) || 30;
      const template = countdown.dataset.template || '';
      const tick = () => {
        countdown.textContent = template.replace('__SECONDS__', String(left));
        if (left > 0) { left -= 1; setTimeout(tick, 1000); }
      };
      tick();
    }
  });

  /* ——— PDP gallery: swap the main image from a thumb ——— */
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-gallery-thumb]');
    if (!thumb) return;
    e.preventDefault();
    const root = thumb.closest('[data-gallery]');
    const main = root && qs('[data-gallery-main] img', root);
    const src = thumb.getAttribute('data-full') || qs('img', thumb)?.src;
    if (main && src) {
      main.src = src;
      main.srcset = '';
    }
    qsa('[data-gallery-thumb]', root).forEach((t) => t.classList.toggle('is-active', t === thumb));
  });

  /* ——— PDP sticky bar: show once the main add-to-cart scrolls away ——— */
  qsa('[data-sticky-bar]').forEach((bar) => {
    const anchor = qs('[data-sticky-anchor]');
    if (!anchor || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => bar.classList.toggle('is-visible', !entry.isIntersecting),
      { rootMargin: '0px 0px -40% 0px' }
    );
    io.observe(anchor);
  });

  /* ——— Product rail ———
     Native overflow + scroll-snap carry it; the arrows are enhancement and
     stay hidden when there is nothing to scroll. ——— */
  qsa('[data-rail]').forEach((rail) => {
    const track = qs('[data-rail-track]', rail);
    const arrows = qs('[data-rail-arrows]', rail);
    if (!track || !arrows) return;

    const page = () => Math.max(track.clientWidth * 0.8, 240);

    function sync() {
      const scrollable = track.scrollWidth - track.clientWidth > 4;
      arrows.hidden = !scrollable;
      if (!scrollable) return;
      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      qs('[data-rail-prev]', rail).disabled = atStart;
      qs('[data-rail-next]', rail).disabled = atEnd;
    }

    qs('[data-rail-prev]', rail)?.addEventListener('click', () =>
      track.scrollBy({ left: -page(), behavior: 'smooth' })
    );
    qs('[data-rail-next]', rail)?.addEventListener('click', () =>
      track.scrollBy({ left: page(), behavior: 'smooth' })
    );
    track.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
    window.addEventListener('resize', sync);
    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(track);
    sync();
  });

  /* ——— Hero video transport ——— */
  qsa('[data-video-controls]').forEach((root) => {
    const host = root.closest('[data-video-root]') || document;
    const video = qs('video', host);
    if (!video) return;
    qs('[data-video-toggle]', root)?.addEventListener('click', (e) => {
      const btn = e.currentTarget;
      if (video.paused) { video.play(); btn.dataset.state = 'playing'; }
      else { video.pause(); btn.dataset.state = 'paused'; }
    });
    qs('[data-video-mute]', root)?.addEventListener('click', (e) => {
      video.muted = !video.muted;
      e.currentTarget.dataset.state = video.muted ? 'muted' : 'unmuted';
    });
  });


  /* ———————————————————————————————————————————————————————————————
     Chain physics

     Every charm is a damped pendulum integrated on rAF, coupled to its
     neighbours through the chain so a nudge travels along the row instead of
     each charm swinging in isolation. That is the difference between this and
     a CSS keyframe: momentum, transfer, and a real settle to rest.

       θ'' = -(g / L)·sin θ  −  c·θ'  +  k·(θₙ₋₁ + θₙ₊₁ − 2θₙ)

     Energy decays, the loop parks itself when the row is still, and any
     interaction (adding a charm, dragging one, the Create Magic flick, or a
     fast scroll) injects fresh angular velocity. Honours prefers-reduced-motion
     by simply never starting.
     ——————————————————————————————————————————————————————————————— */
  const REDUCED = window.matchMedia?.('(prefers-reduced-motion: reduce)');

  function createChainSim(stage) {
    /*
      Tuned by sweeping the integrator offline against three targets: peak swing
      12-20°, comes to rest in under ~3.5s, and the charms drift out of phase
      instead of moving as one rigid comb. Stiff coupling looked like a comb;
      almost none, plus slightly different pendulum lengths, reads as jewellery.
    */
    const G = 26;          // gravity; higher = faster, tighter swing
    const DAMP = 2.4;      // air + friction at the bail; sets the settle time
    const COUPLE = 0.8;    // light chain coupling — enough to nudge neighbours
    const SLEEP = 0.0016;  // below this the row is visually at rest
    const MAX_DT = 1 / 30; // clamp so a backgrounded tab cannot explode the sim

    let parts = [];
    let raf = 0;
    let last = 0;
    let sleeping = true;

    function collect() {
      parts = [...stage.querySelectorAll('.studio__charm-swing')].map((el, i) => ({
        el,
        // deliberately unequal lengths: identical pendulums stay in lockstep
        // forever, which is what made the first attempt look like a comb
        len: 1.05 + i * 0.09,
        theta: (Math.random() - 0.5) * 0.04,
        omega: 0,
        i,
      }));
    }

    function write() {
      for (const p of parts) {
        p.el.style.transform = `rotate(${p.theta.toFixed(4)}rad)`;
      }
    }

    function step(now) {
      const dt = Math.min((now - last) / 1000 || 0, MAX_DT);
      last = now;

      let energy = 0;
      for (let i = 0; i < parts.length; i += 1) {
        const p = parts[i];
        const prev = parts[i - 1];
        const next = parts[i + 1];
        // neighbours share the same chain, so they pull on each other
        let couple = 0;
        if (prev) couple += prev.theta - p.theta;
        if (next) couple += next.theta - p.theta;

        const accel = -(G / p.len) * Math.sin(p.theta) - DAMP * p.omega + COUPLE * couple;
        p.omega += accel * dt;
        p.theta += p.omega * dt;
        energy += p.omega * p.omega + p.theta * p.theta;
      }

      write();

      if (energy < SLEEP) {
        // settle dead level and stop burning frames until something disturbs it
        for (const p of parts) { p.theta = 0; p.omega = 0; }
        write();
        sleeping = true;
        raf = 0;
        return;
      }
      raf = requestAnimationFrame(step);
    }

    function wake() {
      if (REDUCED?.matches || !parts.length) return;
      if (!sleeping) return;
      sleeping = false;
      last = performance.now();
      raf = requestAnimationFrame(step);
    }

    return {
      rebuild() {
        collect();
        write();
      },
      /** Impulse the row. `from` is the index the energy originates at. */
      kick(strength = 1, from = null) {
        if (!parts.length) return;
        const origin = from == null ? (parts.length - 1) / 2 : from;
        for (const p of parts) {
          // energy falls off with distance from the origin, like a real knock
          const falloff = 1 / (1 + Math.abs(p.i - origin) * 0.85);
          p.omega += strength * falloff * (p.i < origin ? -1 : 1) * (0.9 + Math.random() * 0.2);
        }
        wake();
      },
      drag(index, theta) {
        const p = parts[index];
        if (!p) return;
        p.theta = theta;
        p.omega = 0;
        wake();
        write();
      },
      release(index, velocity) {
        const p = parts[index];
        if (!p) return;
        p.omega = velocity;
        wake();
      },
      indexOf(el) {
        return parts.findIndex((p) => p.el === el || p.el.contains(el));
      },
      stop() {
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        sleeping = true;
      },
      get count() { return parts.length; },
    };
  }

  /* ———————————————————————————————————————————————————————————————
     Customization Studio — Figma "Customization Tool" (1897:8937 / 2225:15047)

     One state object drives every step. The stage lays charms out along the
     chain by `left` offset only, so the swing animation owns `transform`
     outright and never fights the positioning maths — that is what keeps the
     dangle buttery instead of janky.
     ——————————————————————————————————————————————————————————————— */
  function initStudio(root) {
    const STEPS = ['start', 'type', 'settings', 'charms', 'save'];
    const basePrice = Number(root.dataset.basePrice || 8000);

    const state = {
      step: 'start',
      type: 'Chains',
      chain: { label: '', src: '', price: basePrice },
      charms: [],
      count: 1,
      distance: 22,
      metal: 'Yellow Gold 14K',
      engraving: '',
    };

    const sims = new Map(); // stage element -> chain simulation

    const money = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
    const priceOf = (s) => Number(String(s || '').replace(/[^0-9.]/g, '')) || 0;

    function totalPrice() {
      return charmSlots().reduce((sum, c) => sum + priceOf(c.price), state.chain.price);
    }

    function setStep(step) {
      if (!STEPS.includes(step)) return;
      state.step = step;
      qsa('[data-studio-step]', root).forEach((el) => {
        const on = el.dataset.studioStep === step;
        el.hidden = !on;
        el.classList.toggle('is-active', on);
      });
      qsa('[data-studio-goto]', root).forEach((el) => {
        const on = el.dataset.studioGoto === step;
        el.classList.toggle('is-active', on);
        if (el.hasAttribute('aria-current')) el.setAttribute('aria-current', on ? 'step' : 'false');
      });
      sync();
      // charms only re-drop when the step actually changes, so switching tabs
      // doesn't restart the animation mid-swing
      renderCharms({ animate: false });
    }

    function sync() {
      const set = (sel, val) => qsa(sel, root).forEach((el) => { el.value = val; });
      set('[data-studio-prop-type]', state.type);
      set('[data-studio-prop-chain]', state.chain.label);
      set('[data-studio-prop-charms]', state.charms.map((c) => c.label).join(', ') || 'None');
      set('[data-studio-prop-count]', String(state.count));
      set('[data-studio-prop-distance]', String(state.distance));
      set('[data-studio-prop-metal]', state.metal);
      set('[data-studio-prop-engraving]', state.engraving);
      set('[data-studio-prop-components]', String(1 + charmSlots().length));

      qsa('[data-studio-count]', root).forEach((el) => {
        el.textContent = String(state.count).padStart(2, '0');
      });
      qsa('[data-studio-distance-out]', root).forEach((el) => {
        el.textContent = String(Math.round(state.distance / 7)).padStart(2, '0');
      });

      const added = 1 + charmSlots().length;
      qsa('[data-studio-added-count]', root).forEach((el) => {
        el.textContent = `${added} Product${added === 1 ? '' : 's'} Added`;
      });
      qsa('[data-studio-total]', root).forEach((el) => { el.textContent = money(totalPrice()); });
      qsa('[data-studio-base]', root).forEach((img) => {
        if (state.chain.src) img.src = state.chain.src;
      });
      qsa('[data-studio-product-name]', root).forEach((el) => {
        el.innerHTML = `${escapeHtml(state.chain.label)} <span>14k</span>`;
      });
      renderDrawer();
    }

    /* ——— the stage ———
       Charms hang from the chain: positioned with `left`, rotated by CSS.
       Each gets its own period and phase so the row never swings in lockstep,
       plus a one-shot damped drop when it first appears. */
    // fraction of the stage height the chain sags by between its ends and its
    // lowest point — measured off the Figma stage art
    const CURVE_RATIO = 0.14;

    /*
      One model for the whole studio: `count` charms hang on the chain, cycling
      through whatever the shopper selected. The stage, the running total, the
      review drawer and the cart line properties all read this list, so they can
      never disagree — previously the stage drew only the last-picked charm
      while the price charged for every one of them.
    */
    function charmSlots() {
      if (!state.charms.length) return [];
      return Array.from({ length: state.count }, (_, i) => state.charms[i % state.charms.length]);
    }

    function renderCharms({ animate = true } = {}) {
      const slots = charmSlots();
      qsa('[data-studio-charms]', root).forEach((stage) => {
        stage.innerHTML = '';
        if (!slots.length) return;

        // `distance` is the gap *between* charms; add the charm's own width so
        // neighbours never overlap however tight the slider goes
        const CHARM_W = 64;
        const step = CHARM_W * 0.75 + state.distance;
        const spread = Math.max(0, (slots.length - 1) * step);
        const half = Math.max(1, (slots.length - 1) / 2);
        // the chain sags in a V, so charms nearer the centre have to drop
        // further to stay seated on it. Depth scales with the stage so the
        // curve holds at every breakpoint.
        const depth = stage.offsetHeight * CURVE_RATIO;

        slots.forEach((charm, i) => {
          const slot = document.createElement('div');
          slot.className = 'studio__charm is-grabbable';
          // centre the row, then step each charm along the chain
          slot.style.left = `calc(50% - ${spread / 2}px + ${i * step}px)`;
          const t = (i - (slots.length - 1) / 2) / half;
          slot.style.setProperty('--dy', `${((1 - t * t) * depth).toFixed(1)}px`);

          const swing = document.createElement('div');
          swing.className = 'studio__charm-swing';
          // 2.4–3.2s periods and offset phases read as independent pendulums
          swing.style.setProperty('--period', `${(2.4 + ((i * 7) % 9) * 0.09).toFixed(2)}s`);
          swing.style.setProperty('--amp', `${(2.4 + ((i * 5) % 7) * 0.22).toFixed(2)}deg`);
          swing.style.setProperty('--delay', `${(i * 0.11).toFixed(2)}s`);
          if (!animate) swing.classList.add('is-settled');

          const img = document.createElement('img');
          img.src = charm.src;
          img.alt = charm.label;
          img.width = 64;
          img.height = 80;
          img.loading = 'lazy';

          swing.appendChild(img);
          slot.appendChild(swing);
          stage.appendChild(slot);
        });

        let sim = sims.get(stage);
        if (!sim) { sim = createChainSim(stage); sims.set(stage, sim); }
        sim.rebuild();
        // a charm that has just dropped onto the chain sets the row moving
        if (animate) sim.kick(1.2, slots.length - 1);
      });
    }

    function renderDrawer() {
      const list = qs('[data-studio-drawer-list]', root);
      const countEl = qs('[data-studio-drawer-count]', root);
      if (!list) return;
      const items = [
        { src: state.chain.src, label: `${state.chain.label} 18 karat`, price: money(state.chain.price) },
        ...charmSlots().map((c) => ({ src: c.src, label: c.label, price: c.price })),
      ];
      if (countEl) countEl.textContent = `(${items.length} item${items.length === 1 ? '' : 's'})`;
      list.innerHTML = items
        .map(
          (it) => `
        <div class="studio__drawer-card">
          <span class="studio__drawer-media"><img src="${escapeHtml(it.src)}" alt="" width="49" height="59" loading="lazy"></span>
          <div class="studio__drawer-info">
            <p class="studio__drawer-name">${escapeHtml(it.label)}</p>
            <p class="studio__drawer-price">${escapeHtml(it.price)}</p>
          </div>
        </div>`
        )
        .join('');
    }

    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-studio-begin]')) { setStep('type'); return; }

      const goto = e.target.closest('[data-studio-goto]');
      if (goto) { setStep(goto.dataset.studioGoto); return; }

      const typeBtn = e.target.closest('[data-studio-type]');
      if (typeBtn) {
        qsa('[data-studio-type]', root).forEach((b) => b.classList.remove('is-selected'));
        typeBtn.classList.add('is-selected');
        state.type = typeBtn.dataset.label || 'Chains';
        sync();
        return;
      }

      const chainBtn = e.target.closest('[data-studio-chain]');
      if (chainBtn) {
        qsa('[data-studio-chain]', root).forEach((b) => b.classList.remove('is-selected'));
        chainBtn.classList.add('is-selected');
        state.chain = {
          label: chainBtn.dataset.chainLabel,
          src: chainBtn.dataset.chainSrc,
          price: priceOf(chainBtn.dataset.chainPrice) || basePrice,
        };
        sync();
        return;
      }

      const charmBtn = e.target.closest('[data-studio-charm]');
      if (charmBtn) {
        const id = charmBtn.dataset.charmId;
        const existing = state.charms.find((c) => c.id === id);
        if (existing) {
          state.charms = state.charms.filter((c) => c.id !== id);
          charmBtn.classList.remove('is-selected');
          charmBtn.setAttribute('aria-pressed', 'false');
        } else {
          state.charms.push({
            id,
            src: charmBtn.dataset.charmSrc,
            label: charmBtn.dataset.charmLabel,
            price: charmBtn.dataset.charmPrice,
          });
          charmBtn.classList.add('is-selected');
          charmBtn.setAttribute('aria-pressed', 'true');
        }
        if (state.count < 1) state.count = 1;
        renderCharms();
        sync();
        return;
      }

      const cat = e.target.closest('[data-studio-cat]');
      if (cat) {
        qsa('[data-studio-cat]', root).forEach((b) => b.classList.remove('is-active'));
        cat.classList.add('is-active');
        const key = cat.dataset.studioCat;
        qsa('[data-studio-charm]', root).forEach((btn) => {
          btn.style.display = key === 'all' || btn.dataset.cat === key ? '' : 'none';
        });
        return;
      }

      const settingsTab = e.target.closest('[data-studio-settings-tab]');
      if (settingsTab) {
        const key = settingsTab.dataset.studioSettingsTab;
        qsa('[data-studio-settings-tab]', root).forEach((b) => b.classList.toggle('is-active', b === settingsTab));
        qsa('[data-studio-settings-panel]', root).forEach((p) => {
          p.hidden = p.dataset.studioSettingsPanel !== key;
        });
        return;
      }

      if (e.target.closest('[data-studio-count-inc]')) {
        state.count = Math.min(10, state.count + 1);
        renderCharms();
        sync();
        return;
      }
      if (e.target.closest('[data-studio-count-dec]')) {
        state.count = Math.max(1, state.count - 1);
        renderCharms();
        sync();
        return;
      }

      if (e.target.closest('[data-studio-magic]')) {
        // flick the chain from one end so the swing travels along the row
        sims.forEach((sim) => sim.kick(2.4));
        return;
      }

      const saveNext = e.target.closest('[data-studio-save-next]');
      if (saveNext) {
        const key = saveNext.dataset.studioSaveNext;
        // Only claim the design is saved once it actually is. There is no
        // design-storage backend, so it is persisted to this browser and the
        // account's Saved Designs list reads the same store.
        if (key === 'done' && !persistDesign()) return;
        qsa('[data-studio-save-step]', root).forEach((el) => {
          const on = el.dataset.studioSaveStep === key;
          el.hidden = !on;
          el.classList.toggle('is-active', on);
        });
        return;
      }

      if (e.target.closest('[data-studio-drawer-open]')) {
        const d = qs('[data-studio-drawer]', root);
        if (d) { d.hidden = false; renderDrawer(); }
        return;
      }
      if (e.target.closest('[data-studio-drawer-close]')) {
        const d = qs('[data-studio-drawer]', root);
        if (d) d.hidden = true;
        return;
      }

      if (e.target.closest('[data-studio-drawer-add]')) {
        // the drawer's CTA is outside the form, so hand off to the real one —
        // requestSubmit (not submit) so the delegated submit handler still runs
        const d = qs('[data-studio-drawer]', root);
        if (d) d.hidden = true;
        qs('form[data-studio-form]', root)?.requestSubmit();
      }
    });

    // the sag is measured from the rendered stage, so re-seat on resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => renderCharms({ animate: false }), 150);
    });

    qs('[data-studio-distance]', root)?.addEventListener('input', (e) => {
      state.distance = Number(e.target.value) || 22;
      renderCharms({ animate: false });
      sync();
    });
    qs('[data-studio-metal]', root)?.addEventListener('change', (e) => { state.metal = e.target.value; sync(); });
    qs('[data-studio-engraving]', root)?.addEventListener('input', (e) => { state.engraving = e.target.value; sync(); });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const d = qs('[data-studio-drawer]', root);
      if (d) d.hidden = true;
    });

    function persistDesign() {
      const name = qs('[data-studio-design-name]', root)?.value.trim();
      // the message has to land in the step that is on screen — the name step
      // is display:none by the time we validate
      const visibleStep = qsa('[data-studio-save-step]', root).find((el) => !el.hidden) || root;
      if (!name) {
        showFormError(visibleStep, 'Please name your design first — go back a step.');
        return false;
      }
      const entry = {
        id: `${name}-${state.chain.label}-${state.charms.length}`,
        name,
        chain: state.chain,
        charms: state.charms,
        count: state.count,
        distance: state.distance,
        metal: state.metal,
        engraving: state.engraving,
        total: totalPrice(),
      };
      try {
        const stored = JSON.parse(localStorage.getItem(SAVED_DESIGNS_KEY) || '[]');
        const all = Array.isArray(stored) ? stored.filter((d) => d && d.id !== entry.id) : [];
        localStorage.setItem(SAVED_DESIGNS_KEY, JSON.stringify([...all, entry]));
        return true;
      } catch (err) {
        console.error(err);
        showFormError(visibleStep, 'Could not save on this device. Try again outside private browsing.');
        return false;
      }
    }

    /* ——— pick a charm up and swing it ———
       Pointer angle drives θ directly; releasing hands the sim the angular
       velocity from the last few moves so it carries on with real momentum. */
    qsa('[data-studio-charms]', root).forEach((stage) => {
      let dragging = null;

      stage.addEventListener('pointerdown', (e) => {
        const swing = e.target.closest('.studio__charm-swing');
        if (!swing) return;
        const sim = sims.get(stage);
        if (!sim) return;
        const idx = sim.indexOf(swing);
        if (idx < 0) return;
        const pivot = swing.getBoundingClientRect();
        dragging = {
          sim,
          idx,
          swing,
          px: pivot.left + pivot.width / 2,
          py: pivot.top,
          lastTheta: 0,
          lastT: performance.now(),
          vel: 0,
        };
        stage.classList.add('is-dragging');
        swing.setPointerCapture?.(e.pointerId);
        e.preventDefault();
      });

      stage.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        // angle of the pointer measured from the charm's pivot
        const theta = Math.atan2(e.clientX - dragging.px, Math.max(e.clientY - dragging.py, 8));
        // past ~34° a real charm stops swinging out and starts dragging the
        // chain itself, so clamp rather than let it rotate freely
        const clamped = Math.max(-0.6, Math.min(0.6, theta));
        const now = performance.now();
        const dt = Math.max((now - dragging.lastT) / 1000, 1 / 240);
        dragging.vel = (clamped - dragging.lastTheta) / dt;
        dragging.lastTheta = clamped;
        dragging.lastT = now;
        dragging.sim.drag(dragging.idx, clamped);
      });

      const endDrag = () => {
        if (!dragging) return;
        // hand back the throw velocity, capped so it cannot spin
        dragging.sim.release(dragging.idx, Math.max(-3, Math.min(3, dragging.vel)));
        stage.classList.remove('is-dragging');
        dragging = null;
      };
      stage.addEventListener('pointerup', endDrag);
      stage.addEventListener('pointercancel', endDrag);
      stage.addEventListener('pointerleave', endDrag);
    });

    /* ——— scrolling jostles the piece, like it would in your hand ——— */
    let lastScroll = window.scrollY;
    let scrollTick = false;
    window.addEventListener(
      'scroll',
      () => {
        if (scrollTick) return;
        scrollTick = true;
        requestAnimationFrame(() => {
          const dy = window.scrollY - lastScroll;
          lastScroll = window.scrollY;
          const force = Math.max(-0.45, Math.min(0.45, dy * 0.012));
          if (Math.abs(force) > 0.12) sims.forEach((sim) => sim.kick(force));
          scrollTick = false;
        });
      },
      { passive: true }
    );

    // stop simulating what nobody can see
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) sims.forEach((sim) => sim.stop());
    });

    // seed from the first chain so the stage is never empty
    const firstChain = qs('[data-studio-chain]', root);
    if (firstChain) {
      state.chain = {
        label: firstChain.dataset.chainLabel,
        src: firstChain.dataset.chainSrc,
        price: priceOf(firstChain.dataset.chainPrice) || basePrice,
      };
    }
    const firstType = qs('[data-studio-type]', root);
    if (firstType) state.type = firstType.dataset.label || state.type;

    setStep('start');
  }

  qsa('[data-studio]').forEach(initStudio);
})();

/* ------------------------------------------------------------------ *
 * Home-screen install
 *
 * Android/Chrome fire beforeinstallprompt and let us call prompt() from a
 * user gesture. iOS has no such API — Add to Home Screen is a manual Share
 * menu action — so there we show a short instruction sheet instead. Either
 * way the footer entry stays hidden until it can actually do something.
 * ------------------------------------------------------------------ */
(() => {
  const qsa = (sel, root = document) => [...root.querySelectorAll(sel)];

  const standalone = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;

  const ua = navigator.userAgent || '';
  // iPadOS 13+ reports as Macintosh, so test for touch as well
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  // Add to Home Screen only exists in Safari's share sheet, not in Chrome/Firefox on iOS
  const isIOSSafari = isIOS && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  const isAndroid = /Android/.test(ua);

  let deferredPrompt = null;
  let sheet = null;

  const blocks = () => qsa('[data-install-app]');

  function reveal() {
    if (standalone()) return;
    if (!deferredPrompt && !isIOSSafari && !isAndroid) return;
    blocks().forEach((el) => el.removeAttribute('hidden'));
  }

  function hide() {
    blocks().forEach((el) => el.setAttribute('hidden', ''));
  }

  function closeSheet() {
    if (!sheet) return;
    sheet.classList.remove('is-open');
    document.body.style.overflow = '';
    const el = sheet;
    setTimeout(() => el.remove(), 240);
    sheet = null;
  }

  function steps() {
    if (isAndroid) {
      return [
        'Open the <strong>&#8942;</strong> menu in the browser toolbar.',
        'Choose <strong>Add to Home screen</strong> (or <strong>Install app</strong>).',
        'Confirm, and we&rsquo;ll be on your home screen.',
      ];
    }
    return [
      'Tap the <strong>Share</strong> button in Safari&rsquo;s toolbar.',
      'Scroll down and choose <strong>Add to Home Screen</strong>.',
      'Tap <strong>Add</strong>. We&rsquo;ll be on your home screen.',
    ];
  }

  function openInstructions() {
    if (sheet) return;
    sheet = document.createElement('div');
    sheet.className = 'install-sheet';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-label', 'Install this app');
    sheet.innerHTML = `
      <div class="install-sheet__scrim" data-close></div>
      <div class="install-sheet__panel" role="document">
        <button type="button" class="install-sheet__close" data-close aria-label="Close">&times;</button>
        <p class="install-sheet__eyebrow">Add to Home Screen</p>
        <h2 class="install-sheet__title">Keep us a tap away</h2>
        <ol class="install-sheet__steps">
          ${steps().map((s, i) => `
            <li><span class="install-sheet__num">${i + 1}</span><span>${s}</span></li>
          `).join('')}
        </ol>
        <p class="install-sheet__note">Opens full screen, with no browser bars.</p>
      </div>`;
    document.body.appendChild(sheet);
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => sheet.classList.add('is-open'));
    sheet.addEventListener('click', (e) => {
      if (e.target.closest('[data-close]')) closeSheet();
    });
    sheet.querySelector('.install-sheet__close')?.focus();
  }

  async function install() {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      deferredPrompt = null;
      if (outcome === 'accepted') hide();
      return;
    }
    if (isIOSSafari || isAndroid) openInstructions();
  }

  window.addEventListener('beforeinstallprompt', (e) => {
    // keep the event so the footer button can trigger it later
    e.preventDefault();
    deferredPrompt = e;
    reveal();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    hide();
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-install-trigger]')) {
      e.preventDefault();
      install();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeSheet();
  });

  // iOS never fires beforeinstallprompt, so surface the entry straight away
  if (isIOSSafari || isAndroid) reveal();
  document.addEventListener('shopify:section:load', reveal);

  if (standalone()) document.documentElement.classList.add('is-standalone');
})();

/* ------------------------------------------------------------------ *
 * Live chain
 *
 * Replaces the flat chain artwork with a rope solved every frame, so the
 * necklace hangs from above the stage, drapes into a real catenary and
 * carries the charms with it.
 *
 * Verlet integration rather than a spring model: positions are advanced from
 * their own history and the segment lengths are then relaxed back to true over
 * several passes. That is what makes a chain read as inextensible — springs
 * stretch and wobble, chains do not.
 *
 * It drives the existing .studio__charm nodes rather than rendering its own, so
 * the pricing/cart model in the studio code above stays the single source of
 * truth. A MutationObserver picks up whatever that code renders.
 * ------------------------------------------------------------------ */
(() => {
  const stages = [...document.querySelectorAll('[data-studio-stage]')];
  if (!stages.length) return;

  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SVG_NS = 'http://www.w3.org/2000/svg';

  const NODES = 46;        // rope resolution; more looks smoother, costs more
  const RELAX = 14;        // constraint passes per frame — chains need a lot
  const GRAVITY = 2100;    // px/s², tuned against the stage height not real life
  const DRAG = 0.994;      // velocity retained per frame; the chain's air losses
  const SPAN = 0.74;       // anchor separation as a fraction of stage width
  const RISE = 0.30;       // how far above the stage the anchors sit — the chain
                           // comes down from off-screen rather than starting at
                           // the top edge
  const SLACK = 2.85;      // rope length as a multiple of the anchor span; this
                           // is what sets how deep the necklace hangs
  const CHARM_DROP = 26;   // pendulum length from the chain to a charm's centre

  function createRope(stage) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'studio__rope');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('preserveAspectRatio', 'none');

    const defs = document.createElementNS(SVG_NS, 'defs');
    const grad = document.createElementNS(SVG_NS, 'linearGradient');
    const gradId = `ropeGold-${Math.round(performance.now() * 1000) % 1e6}`;
    grad.setAttribute('id', gradId);
    grad.setAttribute('x1', '0');
    grad.setAttribute('y1', '0');
    grad.setAttribute('x2', '1');
    grad.setAttribute('y2', '1');
    [
      ['0%', '#8d6b3f'],
      ['22%', '#e8cd93'],
      ['48%', '#b8914f'],
      ['74%', '#f0dcae'],
      ['100%', '#8a6636'],
    ].forEach(([off, col]) => {
      const s = document.createElementNS(SVG_NS, 'stop');
      s.setAttribute('offset', off);
      s.setAttribute('stop-color', col);
      grad.appendChild(s);
    });
    defs.appendChild(grad);
    svg.appendChild(defs);

    // Three stacked strokes: a dark core for depth, the gold body, and a fine
    // highlight. Cheaper than drawing every link as its own element, and at
    // this scale it reads the same.
    const shadow = document.createElementNS(SVG_NS, 'path');
    shadow.setAttribute('fill', 'none');
    shadow.setAttribute('stroke', 'rgba(58,40,18,0.34)');
    shadow.setAttribute('stroke-linecap', 'round');

    const body = document.createElementNS(SVG_NS, 'path');
    body.setAttribute('fill', 'none');
    body.setAttribute('stroke', `url(#${gradId})`);
    body.setAttribute('stroke-linecap', 'round');

    // The dashes are the links. Dash length tracks stroke width so the chain
    // keeps its proportions when the stage resizes.
    const links = document.createElementNS(SVG_NS, 'path');
    links.setAttribute('fill', 'none');
    links.setAttribute('stroke', 'rgba(255,248,232,0.72)');
    links.setAttribute('stroke-linecap', 'round');

    svg.append(shadow, body, links);
    stage.appendChild(svg);

    let w = 0;
    let h = 0;
    let pts = [];
    let seg = 0;
    let charms = [];   // { el, swing, node, theta, otheta }
    let raf = 0;
    let last = 0;
    let idle = 0;
    let grabbed = null;

    function measure() {
      const r = stage.getBoundingClientRect();
      w = r.width;
      h = r.height;
      svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      const sw = Math.max(2.4, w * 0.0075);
      shadow.setAttribute('stroke-width', sw * 1.5);
      body.setAttribute('stroke-width', sw);
      links.setAttribute('stroke-width', sw * 0.34);
      links.setAttribute('stroke-dasharray', `${(sw * 0.5).toFixed(2)} ${(sw * 0.92).toFixed(2)}`);
    }

    function anchors() {
      return [
        { x: w * (0.5 - SPAN / 2), y: -h * RISE },
        { x: w * (0.5 + SPAN / 2), y: -h * RISE },
      ];
    }

    function build() {
      measure();
      if (!w || !h) return;
      const [a, b] = anchors();
      seg = (Math.hypot(b.x - a.x, b.y - a.y) * SLACK) / (NODES - 1);
      pts = [];
      for (let i = 0; i < NODES; i += 1) {
        const t = i / (NODES - 1);
        // seed on a parabola close to the resting drape so the first frame
        // doesn't visibly fall into place
        const x = a.x + (b.x - a.x) * t;
        const sag = Math.sin(Math.PI * t) * (seg * NODES * 0.34);
        const y = a.y + (b.y - a.y) * t + sag;
        pts.push({ x, y, px: x, py: y });
      }
      settle(60);
    }

    /** Run the solver without drawing, to reach equilibrium. */
    function settle(steps) {
      for (let i = 0; i < steps; i += 1) {
        integrate(1 / 60);
        constrain();
      }
      draw();
    }

    function integrate(dt) {
      const d = DRAG;
      for (let i = 0; i < pts.length; i += 1) {
        const p = pts[i];
        const vx = (p.x - p.px) * d;
        const vy = (p.y - p.py) * d;
        p.px = p.x;
        p.py = p.y;
        p.x += vx;
        p.y += vy + GRAVITY * dt * dt;
      }
    }

    function constrain() {
      const [a, b] = anchors();
      for (let k = 0; k < RELAX; k += 1) {
        for (let i = 0; i < pts.length - 1; i += 1) {
          const p = pts[i];
          const q = pts[i + 1];
          let dx = q.x - p.x;
          let dy = q.y - p.y;
          const d = Math.hypot(dx, dy) || 0.0001;
          const diff = (d - seg) / d / 2;
          dx *= diff;
          dy *= diff;
          p.x += dx;
          p.y += dy;
          q.x -= dx;
          q.y -= dy;
        }
        // pin the ends last so they always win
        pts[0].x = a.x;
        pts[0].y = a.y;
        pts[pts.length - 1].x = b.x;
        pts[pts.length - 1].y = b.y;
        if (grabbed) {
          pts[grabbed.i].x = grabbed.x;
          pts[grabbed.i].y = grabbed.y;
        }
      }
    }

    function pathData() {
      // Catmull-Rom-ish smoothing: draw through segment midpoints so the
      // polyline never shows its corners.
      let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`;
      for (let i = 1; i < pts.length - 1; i += 1) {
        const mx = (pts[i].x + pts[i + 1].x) / 2;
        const my = (pts[i].y + pts[i + 1].y) / 2;
        d += ` Q ${pts[i].x.toFixed(2)} ${pts[i].y.toFixed(2)} ${mx.toFixed(2)} ${my.toFixed(2)}`;
      }
      const e = pts[pts.length - 1];
      d += ` L ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
      return d;
    }

    function draw() {
      if (!pts.length) return;
      const d = pathData();
      shadow.setAttribute('d', d);
      body.setAttribute('d', d);
      links.setAttribute('d', d);

      for (const c of charms) {
        const p = pts[c.node];
        if (!p) continue;
        c.el.style.transform = `translate3d(${(p.x - c.w / 2).toFixed(1)}px, ${p.y.toFixed(1)}px, 0)`;
        if (c.swing) c.swing.style.transform = `rotate(${c.theta.toFixed(4)}rad)`;
      }
    }

    /** Each charm hangs off its node as its own short pendulum. */
    function swingCharms(dt) {
      for (const c of charms) {
        const p = pts[c.node];
        if (!p) continue;
        // the node's own horizontal motion is what drives the charm
        const ax = (p.x - p.px) / Math.max(dt, 1 / 240);
        const target = Math.atan2(ax * 0.06, CHARM_DROP);
        const stiff = 46;
        const damp = 7.5;
        const acc = (target - c.theta) * stiff - c.otheta * damp;
        c.otheta += acc * dt;
        c.theta += c.otheta * dt;
        c.theta = Math.max(-0.5, Math.min(0.5, c.theta));
      }
    }

    function energy() {
      let e = 0;
      for (const p of pts) e += Math.abs(p.x - p.px) + Math.abs(p.y - p.py);
      return e;
    }

    function frame(now) {
      const dt = Math.min((now - last) / 1000 || 1 / 60, 1 / 30);
      last = now;
      integrate(dt);
      constrain();
      swingCharms(dt);
      draw();

      if (!grabbed && energy() < 0.6) {
        idle += 1;
        // a chain keeps micro-moving for a while; only park it once it is
        // genuinely still for a stretch of frames
        if (idle > 40) { raf = 0; return; }
      } else {
        idle = 0;
      }
      raf = requestAnimationFrame(frame);
    }

    function wake() {
      if (REDUCE.matches) { settle(30); return; }
      idle = 0;
      if (raf) return;
      last = performance.now();
      raf = requestAnimationFrame(frame);
    }

    /** Attach whatever charms the studio code has rendered. */
    function sync() {
      const host = stage.querySelector('[data-studio-charms]');
      if (!host) return;
      // the stage may only just have been revealed
      if (!w || !h || !pts.length) build();
      if (!pts.length) return;
      const els = [...host.querySelectorAll('.studio__charm')];
      // spread the charms across the lowest stretch of the chain
      const mid = Math.round((pts.length - 1) / 2);
      const gap = els.length > 1
        ? Math.max(1, Math.min(3, Math.floor((pts.length * 0.42) / els.length)))
        : 0;
      const start = mid - Math.floor(((els.length - 1) * gap) / 2);
      charms = els.map((el, i) => {
        const swing = el.querySelector('.studio__charm-swing');
        return {
          el,
          swing,
          w: el.offsetWidth || 64,
          node: Math.max(1, Math.min(pts.length - 2, start + i * gap)),
          theta: 0,
          otheta: 0,
        };
      });
      stage.classList.toggle('is-roped', true);
      // a charm landing on the chain should visibly load it
      for (const c of charms) {
        const p = pts[c.node];
        if (p) p.py = p.y - 1.6;
      }
      draw();
      wake();
    }

    // --- interaction -------------------------------------------------

    function localPoint(e) {
      const r = stage.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }

    function nearest(pt) {
      let best = -1;
      let bestD = Infinity;
      // never grab the pinned ends
      for (let i = 1; i < pts.length - 1; i += 1) {
        const d = Math.hypot(pts[i].x - pt.x, pts[i].y - pt.y);
        if (d < bestD) { bestD = d; best = i; }
      }
      return bestD < Math.max(46, w * 0.1) ? best : -1;
    }

    stage.addEventListener('pointerdown', (e) => {
      if (REDUCE.matches) return;
      const pt = localPoint(e);
      const i = nearest(pt);
      if (i < 0) return;
      grabbed = { i, x: pt.x, y: pt.y };
      stage.setPointerCapture?.(e.pointerId);
      stage.classList.add('is-dragging');
      wake();
    });

    stage.addEventListener('pointermove', (e) => {
      if (!grabbed) return;
      const pt = localPoint(e);
      grabbed.x = pt.x;
      grabbed.y = pt.y;
      wake();
    });

    function release() {
      if (!grabbed) return;
      grabbed = null;
      stage.classList.remove('is-dragging');
      wake();
    }
    stage.addEventListener('pointerup', release);
    stage.addEventListener('pointercancel', release);
    stage.addEventListener('pointerleave', release);

    return {
      build() { build(); sync(); },
      sync,
      wake,
      /** Lateral impulse, used by scroll and by adding a charm. */
      kick(force) {
        if (REDUCE.matches || !pts.length) return;
        for (let i = 1; i < pts.length - 1; i += 1) {
          const t = i / (pts.length - 1);
          pts[i].px -= force * Math.sin(Math.PI * t);
        }
        wake();
      },
    };
  }

  const ropes = stages.map((stage) => {
    const rope = createRope(stage);
    rope.build();

    // the static artwork is the no-JS fallback; once the rope is live, drop it
    const base = stage.querySelector('[data-studio-base]');
    if (base) base.style.display = 'none';

    const host = stage.querySelector('[data-studio-charms]');
    if (host) {
      new MutationObserver(() => rope.sync()).observe(host, { childList: true });
    }

    // The stage starts at 0x0 — the studio only reveals it a few steps in — so
    // the first real geometry arrives long after load. Rebuild whenever the box
    // changes size, which covers reveal, resize and orientation alike.
    let lastW = 0;
    let lastH = 0;
    new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (!box || !box.width || !box.height) return;
      if (Math.abs(box.width - lastW) < 2 && Math.abs(box.height - lastH) < 2) return;
      lastW = box.width;
      lastH = box.height;
      rope.build();
    }).observe(stage);

    return rope;
  });

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => ropes.forEach((r) => r.build()), 160);
  });

  // Stepping through the studio reveals the stage by toggling classes. That is
  // not a window resize, so watch for it directly and rebuild once the stage
  // actually has a box to draw into.
  const roots = [...document.querySelectorAll('[data-studio]')];
  let stepTimer = 0;
  const rebuildRevealed = () => {
    clearTimeout(stepTimer);
    stepTimer = setTimeout(() => ropes.forEach((r) => r.build()), 120);
  };
  roots.forEach((root) => {
    new MutationObserver(rebuildRevealed).observe(root, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });
  });

  let lastY = window.scrollY;
  let ticking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const dy = window.scrollY - lastY;
        lastY = window.scrollY;
        const f = Math.max(-3.2, Math.min(3.2, dy * 0.09));
        if (Math.abs(f) > 0.25) ropes.forEach((r) => r.kick(f));
        ticking = false;
      });
    },
    { passive: true }
  );

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) ropes.forEach((r) => r.wake());
  });
})();
